import React, { FC, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { defineMessage, useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { BaseStyle, CoreUtils, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { useGamePolling } from '@nhl/react-hooks/lib/useGamePolling';
import * as BlazeSDK from '@wscsports/blaze-web-sdk';

import { CountryCode } from '~/core/enums';

import { ScoresGame } from '../../types';

import { WidgetDataLoadCompletedEventDetails } from './types';

import { GAME_STORY_HEIGHT, GameStoriesContainer, LoadingDisplayContainer } from './styles';

type Props = {
    countryCode?: CountryCode;
    games: Array<ScoresGame>;
};

const BLAZE_CONNECTED_EVENT = 'onBlazeSDKConnect';
const SCORES_LABEL = 'scores-top';

const pregameMessage = defineMessage({
    defaultMessage: 'PRE',
    description:
        'A string which indicates that a "game story" relates to a game which has not started - short for pregame.',
});

const liveGameMessage = defineMessage({
    defaultMessage: 'LIVE',
    description: 'A string which indicates a "game story" relates to a live game.',
});

const postGameMessage = defineMessage({
    defaultMessage: 'POST',
    description: 'A string which indicates that a "game story" relates to a game which is over - short for post game.',
});

const GameStories: FC<Props> = ({ countryCode, games }) => {
    const theme = useTheme();
    const containerId = useId();
    const intl = useIntl();

    const [isWidgetInitialized, setIsWidgetInitialized] = useState<boolean>(false);
    const [hasNoStories, setHasNoStories] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const widgetView = useRef<BlazeSDK.IWidgetView>();

    const generateTheme = useCallback(
        (labelText: string): BlazeSDK.IWidgetTheme => {
            const baseTheme = BlazeSDK.Theme('row-circle');
            baseTheme.layoutStyle.labelStyle.isVisible = false;
            baseTheme.layoutStyle.horizontalItemsSpacing = BaseStyle.GAP_SIZES.semiMedium;
            baseTheme.layoutStyle.borderPadding = 0;
            baseTheme.layoutStyle.isSkeleton = true;

            const statusBaseStyle: BlazeSDK.IndicatorStyle = {
                backgroundColor: theme.Surface.border,
                textColor: theme.Text.OppositeSurface.primary,
                font: 'Inter, arial, sans-serif',
                textSize: '10px',
                letterSpacing: 'initial',
                borderRadius: '14px',
                borderWidth: 3,
                borderColor: theme.Surface.border,
                thumbnailBorderColor: theme.Surface.border,
                text: labelText,
                isVisible: true,
                backgroundImage: '',
                padding: '0 4px',
            };

            baseTheme.layoutStyle.statusLiveStyle = {
                ...statusBaseStyle,
                textColor: theme.Text.primary,
            };

            baseTheme.layoutStyle.statusLiveUnreadStyle = {
                ...statusBaseStyle,
                backgroundColor: theme.Utility.cta,
                borderColor: theme.Utility.cta,
                thumbnailBorderColor: theme.Utility.cta,
            };

            baseTheme.layoutStyle.statusReadStyle = {
                ...statusBaseStyle,
                textColor: theme.Text.primary,
            };

            baseTheme.layoutStyle.statusUnreadStyle = {
                ...statusBaseStyle,
                backgroundColor: theme.Utility.cta,
                borderColor: theme.Utility.cta,
                thumbnailBorderColor: theme.Utility.cta,
            };

            return baseTheme;
        },
        [theme]
    );

    const pregameTheme: BlazeSDK.IWidgetTheme = useMemo(
        () => generateTheme(intl.formatMessage(pregameMessage)),
        [generateTheme, intl]
    );
    const liveGameTheme: BlazeSDK.IWidgetTheme = useMemo(
        () => generateTheme(intl.formatMessage(liveGameMessage)),
        [generateTheme, intl]
    );
    const postGameTheme: BlazeSDK.IWidgetTheme = useMemo(
        () => generateTheme(intl.formatMessage(postGameMessage)),
        [generateTheme, intl]
    );

    const storyLabels = useMemo(
        // if the locale is French then we filter for French stories, otherwise we just use the English ones (ie, no appended locale code).
        () =>
            games?.map(
                (game) => `g${game.id}${intl.locale === ShortCodeEnums.Language.French ? `-${intl.locale}` : ''}`
            ) || [],
        [games, intl]
    );

    const gameStyleOverrides = useMemo(
        () =>
            games.map((game) => {
                let themeForGame: BlazeSDK.IWidgetTheme;

                if (CoreUtils.isPregame(game.gameState)) {
                    themeForGame = pregameTheme;
                } else if (CoreUtils.isGameLive(game.gameState)) {
                    themeForGame = liveGameTheme;
                } else {
                    themeForGame = postGameTheme;
                }

                return {
                    name: `${game.id}`,
                    theme: themeForGame,
                };
            }),
        [games, pregameTheme, liveGameTheme, postGameTheme]
    );

    useEffect(() => {
        widgetView.current?.updateOverrideStyles({
            gameId: gameStyleOverrides,
            // typescript demands these, unfortunately, even though they're empty
            playerId: [],
            teamId: [],
        });

        widgetView.current?.updateWidgetUI();
    }, [gameStyleOverrides]);

    // Initialize the Blaze SDK.
    // This effect will run every time the country code changes (which effectively just means on mount/remount),
    // but the Initialize function only needs to be called if the SDK has not already, at some point, been Initialized.
    useEffect(() => {
        const initializeBlazeSDK = async () => {
            if (window.__SITE_SETTINGS__.blazeAccountKey && countryCode) {
                try {
                    await BlazeSDK.Initialize(window.__SITE_SETTINGS__.blazeAccountKey, {
                        geoLocation: countryCode,
                    });
                } catch {
                    setHasNoStories(true);
                    setIsLoading(false);
                }
            }
        };

        if (!BlazeSDK.isInitialized()) {
            initializeBlazeSDK();
        }
    }, [countryCode]);

    const createWidget = useCallback(() => {
        setIsWidgetInitialized(true);

        let labels: BlazeSDK.BlazeWidgetLabel;

        if (storyLabels.length === 1) {
            labels = BlazeSDK.LabelBuilder().singleLabel(storyLabels[0]);
        } else {
            labels = BlazeSDK.LabelBuilder().atLeastOneOf(...storyLabels);
        }

        labels = labels.mustInclude(SCORES_LABEL);

        widgetView.current = BlazeSDK.WidgetRowView(CSS.escape(containerId), {
            labels,
            theme: pregameTheme,
            delegates: {
                [BlazeSDK.Delegations.onWidgetDataLoadCompleted]: (
                    event: CustomEvent<WidgetDataLoadCompletedEventDetails>
                ) => {
                    setHasNoStories(event.detail?.storyCount === 0);
                    setIsLoading(false);
                },
                [BlazeSDK.Delegations.onWidgetStoryPlayerDismissed]: (_event: CustomEvent) => {
                    widgetView.current?.reload();
                },
            } as Record<BlazeSDK.Delegation, EventListenerOrEventListenerObject>,
            perItemStyleOverrides: {
                gameId: gameStyleOverrides,
                // typescript demands these, unfortunately, even though they're empty
                playerId: [],
                teamId: [],
            },
        });
    }, [containerId, gameStyleOverrides, pregameTheme, storyLabels]);

    useGamePolling(() => widgetView.current?.reload(), games, {
        livePollTime: 1000 * 60, // 1 minute
    });

    // create a widget once the Blaze SDK is connected or if it's already initialized
    useEffect(() => {
        if (!isWidgetInitialized) {
            if (BlazeSDK.isInitialized()) {
                createWidget();
            } else {
                document.addEventListener(BLAZE_CONNECTED_EVENT, createWidget);

                return () => document.removeEventListener(BLAZE_CONNECTED_EVENT, createWidget);
            }
        }

        return undefined;
    }, [createWidget, isWidgetInitialized]);

    return (
        <>
            <LoadingDisplayContainer $isHidden={!isLoading}>
                <LoadingDisplay isLoading={isLoading} isInline size={GAME_STORY_HEIGHT} />
            </LoadingDisplayContainer>
            <GameStoriesContainer $isHidden={hasNoStories} id={containerId} />
        </>
    );
};

export default GameStories;
